/**
 * 品题角度 Catalog — 对齐 Contracts `TtqAngleId` / ttq-angle-model.md
 */
export type TtqSanyAngleId = 'multi_solution' | 'question_chain' | 'pattern_unify';

export type TtqQuestionChainVariantId =
  | 'question_chain'
  | 'condition_generalize'
  | 'condition_specialize'
  | 'conclusion_progression'
  | 'inverse_swap'
  | 'context_transfer'
  | 'element_weak_strong'
  | 'depth_extend'
  | 'error_boundary'
  | 'custom';

export type TtqBehaviorId =
  | TtqSanyAngleId
  | 'questions_chain'
  | 'cttq_converge'
  | 'session_aggregate_a'
  | 'session_aggregate_b';

export type TtqBehaviorKind = 'one_to_many' | 'many_to_one' | 'many_to_many';

export type TtqBehaviorAvailability = 'available' | 'coming_soon' | 'backend_missing';

export type TtqBehaviorTriggerMode = 'direct' | 'guidance';

export type TtqSanyAngleItem = {
  id: TtqSanyAngleId;
  kind: TtqBehaviorKind;
  label: string;
  sanyTag: '一题多解' | '一题多题' | '一解多题';
  description: string;
  availability: TtqBehaviorAvailability;
  triggerMode: TtqBehaviorTriggerMode;
};

export type TtqQuestionChainVariantItem = {
  id: TtqQuestionChainVariantId;
  label: string;
  description: string;
};

export type TtqMultiParticleBehaviorItem = {
  id: Extract<TtqBehaviorId, 'pattern_unify' | 'questions_chain' | 'cttq_converge' | 'session_aggregate_a' | 'session_aggregate_b'>;
  kind: TtqBehaviorKind;
  label: string;
  description: string;
  minParticles: number;
  availability: TtqBehaviorAvailability;
  triggerMode: TtqBehaviorTriggerMode;
};

export type TtqBehaviorListItem = TtqSanyAngleItem | TtqMultiParticleBehaviorItem;

const SANY_ANGLES: TtqSanyAngleItem[] = [
  {
    id: 'multi_solution',
    kind: 'one_to_many',
    label: '一题多解',
    sanyTag: '一题多解',
    description: '同一道题构造不同解法路径，一对多给出同一题的不同求解答法。',
    availability: 'available',
    triggerMode: 'guidance',
  },
  {
    id: 'question_chain',
    kind: 'one_to_many',
    label: '一题多题',
    sanyTag: '一题多题',
    description: '从源题出发改变题面/条件/情境，一对多衍生多道不同题目；含全部 GO-ON 子变式。',
    availability: 'available',
    triggerMode: 'guidance',
  },
];

const SANY_ANGLES_LEGACY_PATTERN_UNIFY: TtqSanyAngleItem = {
  id: 'pattern_unify',
  kind: 'many_to_one',
  label: '一解多题',
  sanyTag: '一解多题',
  description: '解法的收敛：多题综合同解，生成综合知识颗粒（仅鸟瞰多选）。',
  availability: 'available',
  triggerMode: 'guidance',
};

export const TTQ_QUESTION_CHAIN_VARIANTS: TtqQuestionChainVariantItem[] = [
  { id: 'question_chain', label: '同题干递进', description: '同一题干下层层追问，形成递进问题链。' },
  { id: 'condition_generalize', label: '条件一般化', description: '具体数换为参数，突出通性与代数结构。' },
  { id: 'condition_specialize', label: '条件特殊化', description: '字母或参数换为具体数值，巩固基础解法。' },
  { id: 'conclusion_progression', label: '结论递进', description: '更弱或更强的结论；用上题结论作新条件。' },
  { id: 'inverse_swap', label: '逆向变式', description: '变化充分性与必要性，完成条件与结论的角色互换。' },
  { id: 'context_transfer', label: '情景迁移', description: '底层模型不变，更换生活或应用背景。' },
  { id: 'element_weak_strong', label: '要素变式', description: '弱化、强化、增删或开放某些条件/结论。' },
  { id: 'depth_extend', label: '深度变式', description: '分解、延伸或综合，探一般规律或压轴向。' },
  { id: 'error_boundary', label: '易错边界', description: '在易错边界设定辨析题或陷阱变式。' },
  { id: 'custom', label: '自定义拓展', description: '自行描述变式意图。' },
];

/** 暂时不在品题菜单/引导中展示的行为（路由与页面实现保留） */
export const HIDDEN_TTQ_BEHAVIOR_IDS = new Set<TtqBehaviorId>([
  'questions_chain',
  'session_aggregate_b',
]);

export function isTtqBehaviorVisibleInMenu(id: TtqBehaviorId): boolean {
  return !HIDDEN_TTQ_BEHAVIOR_IDS.has(id);
}

function filterVisibleMenuBehaviors<T extends { id: TtqBehaviorId }>(items: T[]): T[] {
  return items.filter((item) => isTtqBehaviorVisibleInMenu(item.id));
}

const MULTI_PARTICLE_BEHAVIORS: TtqMultiParticleBehaviorItem[] = [
  {
    id: 'pattern_unify',
    kind: 'many_to_one',
    label: '一解多题',
    description: '解法的收敛：多题判断能否同解，提炼共用方法核并生成一颗综合知识颗粒（可后续追加 member）。',
    minParticles: 2,
    availability: 'available',
    triggerMode: 'guidance',
  },
  {
    id: 'questions_chain',
    kind: 'one_to_many',
    label: '多题多题',
    description: '对已选多颗源题，按与一题多题相同的 GO-ON 变式维度分别衍生关联题面。',
    minParticles: 2,
    availability: 'available',
    triggerMode: 'guidance',
  },
  {
    id: 'cttq_converge',
    kind: 'many_to_one',
    label: '收敛品题',
    description: '题目的收敛：将多个颗粒收敛为一个代表题，减少复习负担。',
    minParticles: 2,
    availability: 'available',
    triggerMode: 'guidance',
  },
  {
    id: 'session_aggregate_a',
    kind: 'many_to_many',
    label: '共同隐性能力挖掘',
    description: '在多个颗粒间抽取共同隐性能力，形成聚合分析结果。',
    minParticles: 2,
    availability: 'available',
    triggerMode: 'guidance',
  },
  {
    id: 'session_aggregate_b',
    kind: 'many_to_many',
    label: '变式穿插建议',
    description: '基于共同隐性能力给出多颗粒变式穿插练习建议。',
    minParticles: 2,
    availability: 'available',
    triggerMode: 'guidance',
  },
];

export function getTtqSanyAngles(): TtqSanyAngleItem[] {
  return SANY_ANGLES;
}

export function getTtqMultiParticleGuidanceAngles(selectedCount: number) {
  if (selectedCount < 2) return [];
  return filterVisibleMenuBehaviors(
    MULTI_PARTICLE_BEHAVIORS.filter((b) => b.id === 'pattern_unify' || b.id === 'questions_chain'),
  );
}

/** 行为面板：含 CTTQ / A·B / 多题一解多题 */
export function getTtqMultiParticleBehaviors(selectedCount: number): TtqMultiParticleBehaviorItem[] {
  if (selectedCount < 2) return [];
  return filterVisibleMenuBehaviors(
    MULTI_PARTICLE_BEHAVIORS.filter((b) => selectedCount >= b.minParticles),
  );
}

/** 一对多画布：仅单颗 L1 */
export function getTtqDeriveCanvasBehaviors(selectedCount: number): TtqBehaviorListItem[] {
  if (selectedCount !== 1) return [];
  return SANY_ANGLES;
}

/** 鸟瞰：仅多对一；选中 ≥1 即展示菜单项，是否可点由选中数与 minParticles 判定 */
export function getTtqBirdsEyeBehaviors(selectedCount: number): TtqBehaviorListItem[] {
  if (selectedCount < 1) return [];
  return filterVisibleMenuBehaviors(
    MULTI_PARTICLE_BEHAVIORS.filter((b) => b.kind === 'many_to_one'),
  );
}

export function isTtqBirdsEyeBehaviorReady(
  item: TtqBehaviorListItem,
  selectedCount: number,
): boolean {
  const minParticles = 'minParticles' in item ? item.minParticles : 2;
  return selectedCount >= Math.max(2, minParticles);
}

/** @deprecated 使用 getTtqDeriveCanvasBehaviors / getTtqBirdsEyeBehaviors */
export function getTtqBehaviorsForSelection(selectedCount: number): TtqBehaviorListItem[] {
  if (selectedCount <= 0) return [];
  if (selectedCount === 1) return SANY_ANGLES;
  return getTtqBirdsEyeBehaviors(selectedCount);
}

export function describeTtqSelectionMode(selectedCount: number): 'none' | 'single' | 'multiple' {
  if (selectedCount <= 0) return 'none';
  if (selectedCount === 1) return 'single';
  return 'multiple';
}

export function isTtqBehaviorSelectable(item: TtqBehaviorListItem): boolean {
  return item.availability === 'available';
}

export function isTtqSanyAngleId(id: TtqBehaviorId): id is TtqSanyAngleId {
  return id === 'multi_solution' || id === 'question_chain' || id === 'pattern_unify';
}

export function getTtqBehaviorLabel(id: TtqBehaviorId): string {
  const all = [...SANY_ANGLES, SANY_ANGLES_LEGACY_PATTERN_UNIFY, ...MULTI_PARTICLE_BEHAVIORS];
  return all.find((b) => b.id === id)?.label || id;
}
